import { useEffect, useMemo } from 'react';

import {
  errorIsClientFailure,
  errorIsRetryableFailure,
} from '@kaiizen/zod-less-api';

import { useKaiProApiClient } from '../../../useKaiProApiClient';

import { usePropertyProject } from '../../../apiState/usePropertyProjects';
import { useScanPhotoSyncModel } from '../Models';
import { usePhotoGallery } from './PhotoGalleryContext';

const BATCH_SIZE = 100;
const BYTES_500MB = 500 * 1024 * 1024;
const BYTES_1GB = 1024 * 1024 * 1024;

const chunk = <T>(array: T[], size: number): T[][] => array.length ? [array.slice(0, size), ...chunk(array.slice(size), size)] : [];

// Concurrency guard — only one reconciliation at a time
let reconcileInProgress: Promise<ManageScanPhotosSyncResult> | null = null;

export type ManageScanPhotosSyncResult = {
  numPhotos: number;
  numSynced: number;
  numUnsynced: number;
  numMoved: number;
  numResyncing: number;
  numDeleted: number;
  bytesFreed: number;
  errors: unknown[];
};

/**
 * Given our usable space (free + our cache), compute the ideal minimum free space.
 * Returns a value between 500MB and 1GB:
 *   usableSpace <= 1GB  → 500MB
 *   usableSpace >= 2GB  → 1GB
 *   in between          → usableSpace / 2
 */
export const computeTargetMinFree = (usableSpace: number): number => Math.max(BYTES_500MB, Math.min(BYTES_1GB, usableSpace / 2));

export const useManageScanPhotosSync = () => {
  const kaiProApiClient = useKaiProApiClient();
  const gallery = usePhotoGallery();
  const { addScanPhotoToSync } = useScanPhotoSyncModel();

  return useMemo(() => {
    const manageScanPhotosSync = async (
      scanIdOrScanIds?: string | string[],
      options?: { deleteSyncedPhotos?: boolean },
    ): Promise<ManageScanPhotosSyncResult> => {
      const startTime = Date.now();
      if (reconcileInProgress) return reconcileInProgress;

      const promise = _manageScanPhotosSync(scanIdOrScanIds, options);
      reconcileInProgress = promise;
      try {
        return await promise;
      } finally {
        reconcileInProgress = null;
      }
    };

    const _manageScanPhotosSync = async (
      scanIdOrScanIds?: string | string[],
      options?: { deleteSyncedPhotos?: boolean },
    ): Promise<ManageScanPhotosSyncResult> => {
      const startTime = Date.now();
      const { deleteSyncedPhotos = false } = options ?? {};

      let result: ManageScanPhotosSyncResult = {
        numPhotos: 0,
        numSynced: 0,
        numUnsynced: 0,
        numMoved: 0,
        numResyncing: 0,
        numDeleted: 0,
        bytesFreed: 0,
        errors: [],
      };

      // this fetches a fresh index from the disk
      const unsyncedPhotoIdsByScanId = await gallery.getUnsyncedPhotoIdsByScanId();

      for (
        const [
          scanId,
          unsyncedPhotoIds,
        ] of unsyncedPhotoIdsByScanId.entries()
      ) {
        try {
          // 1. Get unsynced photos and check which the server already has
          result.numPhotos += unsyncedPhotoIds.length;

          if (unsyncedPhotoIds.length > 0) {
            // Verify current user can create photos on this scan before processing
            try {
              const { canCreate } = await kaiProApiClient.canCreateScanPhoto(scanId);
              if (!canCreate) {
                console.warn(
                  `manageScanPhotosSync: skipping scan ${scanId} — user cannot create photos`,
                );
                continue;
              }
            } catch (error) {
              if (errorIsRetryableFailure(error)) {
                console.warn(
                  `manageScanPhotosSync: skipping scan ${scanId} — retryable error`,
                  error,
                );
                continue;
              }
              if (errorIsClientFailure(error)) {
                console.error(
                  `manageScanPhotosSync: skipping scan ${scanId} — no access (${error.status})`,
                );
                continue;
              }
              throw error; // server/network error — let outer catch handle it
            }

            const syncedPhotoIds = new Set<string>();
            const uncheckedPhotoIds = new Set<string>();
            let scanSkipped = false;
            for (const batch of chunk(unsyncedPhotoIds, BATCH_SIZE)) {
              try {
                const photos = await kaiProApiClient.getScanPhotosById(
                  batch as string[],
                  { validate: true },
                );
                photos.forEach((photo: { id: string }) => syncedPhotoIds.add(photo.id));
              } catch (error) {
                if (errorIsClientFailure(error)) {
                  // Permissions/not-found — current user can't access this scan; skip entirely
                  console.warn(
                    `manageScanPhotosSync: skipping scan ${scanId} — client error (${error.status})`,
                    error,
                  );
                  scanSkipped = true;
                } else if (errorIsRetryableFailure(error)) {
                  console.warn(
                    `manageScanPhotosSync: skipping scan ${scanId} — retryable error`,
                    error,
                  );
                } else {
                  console.error(
                    `manageScanPhotosSync: batch check failed for scan ${scanId}`,
                    error,
                  );
                }
                result.errors.push(error);
                batch.forEach(id => uncheckedPhotoIds.add(id));
              }
            }

            if (scanSkipped) continue;

            // 2. Move confirmed-synced to cache, re-queue the rest
            for (const photoId of unsyncedPhotoIds) {
              try {
                if (uncheckedPhotoIds.has(photoId)) {
                  // Batch check failed — re-queue so the next sync cycle retries
                  await addScanPhotoToSync(scanId, photoId);
                  result.numResyncing++;
                } else if (syncedPhotoIds.has(photoId)) {
                  result.numSynced++;
                  await gallery.markPhotoSynced(scanId, photoId);
                  result.numMoved++;
                } else {
                  result.numUnsynced++;
                  await addScanPhotoToSync(scanId, photoId);
                  result.numResyncing++;
                }
              } catch (error) {
                console.error(
                  `manageScanPhotosSync: failed to process photo ${photoId} for scan ${scanId}`,
                  error,
                );
                result.errors.push(error);
              }
            }
          }
        } catch (error) {
          console.error(
            `manageScanPhotosSync: unexpected error for scan ${scanId}`,
            error,
          );
          result.errors.push(error);
        }
      }

      // 3. Delete unsynced photos older than retention limit (stuck/un-syncable)
      try {
        const expired = await gallery.deleteExpiredUnsyncedPhotos();
        result.numDeleted += expired.numDeleted;
        result.bytesFreed += expired.bytesFreed;
        if (expired.numDeleted > 0) {
          console.log(
            `manageScanPhotosSync: deleted ${expired.numDeleted} expired unsynced photos (${(expired.bytesFreed / 1024 / 1024).toFixed(1)}MB)`,
          );
        }
      } catch (error) {
        console.error(
          'manageScanPhotosSync: failed to delete expired unsynced photos',
          error,
        );
      }

      // 4. Clean up empty scan directories in documentDirectory
      try {
        await gallery.cleanupEmptyScanDirectories();
      } catch (error) {
        console.error(
          'manageScanPhotosSync: failed to clean up empty directories',
          error,
        );
      }

      try {
        const cleanup = await (deleteSyncedPhotos
          ? gallery.clearPhotoCache()
          : runDiskPressureCleanup());
        result.numDeleted += cleanup.numDeleted;
        result.bytesFreed += cleanup.bytesFreed;
      } catch (error) {
        console.error(
          `manageScanPhotosSync: ${deleteSyncedPhotos ? 'deleteSyncedPhotos' : 'runDiskPressureCleanup'} failed`,
          error,
        );
      }

      const duration = Date.now() - startTime;
      const actuallyDidSomething = result.numDeleted > 0 || result.numMoved > 0 || result.numResyncing > 0;
      const wasSlow = duration > 5000;

      console[wasSlow ? 'warn' : actuallyDidSomething ? 'info' : 'log'](
        wasSlow ? 'manageScanPhotosSync successful but slow' : 'manageScanPhotosSync successful',
        { ...result, scanIdOrScanIds, options, duration },
      );

      // ensure we refresh the photo indexes
      await gallery.getUnsyncedPhotoIdsByScanId();

      return result;
    };

    // Only called after the null guard in _manageScanPhotosSync, so these are guaranteed non-null
    const runDiskPressureCleanup = async (): Promise<{
      bytesFreed: number;
      numDeleted: number;
    }> => {
      const diskInfo = await gallery.getPhotoGalleryStorageInfo();
      if (!diskInfo) return { bytesFreed: 0, numDeleted: 0 };

      const { freeStorageSpace } = diskInfo;
      const galleryInfo = await gallery.getPhotoGalleryInfo();
      const ourCacheSize = galleryInfo.cachedPhotos.numBytes;
      const usableSpace = freeStorageSpace + ourCacheSize;
      const targetMinFree = computeTargetMinFree(usableSpace);

      if (freeStorageSpace >= targetMinFree) {
        return { bytesFreed: 0, numDeleted: 0 };
      }

      const desiredFreeSpace = targetMinFree * 2;
      const bytesToFree = Math.min(
        desiredFreeSpace - freeStorageSpace,
        ourCacheSize,
      );

      if (bytesToFree <= 0) return { bytesFreed: 0, numDeleted: 0 };

      console.log(
        `manageScanPhotosSync: disk pressure cleanup — free: ${(freeStorageSpace / BYTES_1GB).toFixed(2)}GB, target: ${
          (desiredFreeSpace / BYTES_1GB).toFixed(2)
        }GB, freeing: ${(bytesToFree / BYTES_1GB).toFixed(2)}GB`,
      );
      return gallery.freeCacheSpace(bytesToFree);
    };

    return {
      manageScanPhotosSync,
      cleanupSyncedScanPhotos: (scanIdOrScanIds: string | string[]) => manageScanPhotosSync(scanIdOrScanIds, { deleteSyncedPhotos: true }),
      resyncScanPhotos: (scanIdOrScanIds: string | string[]) => manageScanPhotosSync(scanIdOrScanIds),
    };
  }, [addScanPhotoToSync, gallery, kaiProApiClient]);
};

/**
 * Automatically delete local photos which are confirmed-synced to the server for all scans in a project.
 * To conditionally run: pass in a valid projectId TO RUN, pass in undefined to NOT RUN.
 */
export const useAutoCleanupSyncedProjectPhotos = (projectId?: string) => {
  const { propertyProject } = usePropertyProject(projectId);
  const { cleanupSyncedScanPhotos } = useManageScanPhotosSync();
  const scanIds = propertyProject?.scanIds;

  useEffect(() => {
    if (scanIds) {
      cleanupSyncedScanPhotos(scanIds);
    }
  }, [scanIds, cleanupSyncedScanPhotos]);
};
